import classNames from 'classnames';
import { Avatar, Label } from 'flowbite-react';
import React from 'react';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { HiAcademicCap } from 'react-icons/hi';
import { PiInfoFill } from 'react-icons/pi';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatCareerPathType, isScoreRangeValid, stringAvatar } from 'shared-utils';
import { badgesList } from 'src/components/profile/ProfileTechnicalScorecard';
import VerifiedTooltip from 'src/components/profile/VerifiedTooltip';
import { LightTooltip } from 'src/components/recruiterPositions/RecruiterCandidateCardHeader';
import { useThemeContext } from 'src/context/ThemeProvider';
import {
	CampusUserWithApplication,
	GetHiringAssessmentListResponse,
	MeetingStatus,
	TypeOfUsers,
	UserCareerPathType,
	UserPercentileBucket,
} from 'types';
import { PositionCandidatesTabsEnum } from '../../freshersPosition/PositionCandidateListingTabs';
import FresherActionButton from '../../freshers/FresherActionButton';
import CampusCandidateActionButton from './CampusCandidateActionButton';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { candidateTableColumnHeaderType, getBgColor } from './CandidateList';
import { Tooltip } from '@mui/material';
import { useQueryClient } from 'react-query';
import DriveCandidatesActionButton from '../../driveCandidates/DriveCandidatesActionButton';
import MatchPercentileCircularBar from 'src/components/driveAdmin/offlineDriveCompaniesList/MatchPercentileCircularBar';

const CandidateCardMobile = ({
	studentData,
	bulkSelect,
	typeOfUsers,
	selectedTab,
	tableColumns,
	isPositionCreated,
	showCandidateName,
	bulkSelectedCandidates,
	setBulkSelectedCandidates,
	totalShortlistedCandidates,
}: {
	bulkSelect: boolean;
	typeOfUsers: TypeOfUsers;
	isPositionCreated?: boolean;
	showCandidateName?: boolean;
	bulkSelectedCandidates: string[];
	totalShortlistedCandidates?: number;
	studentData: CampusUserWithApplication;
	selectedTab?: CandidatesTabsEnum | PositionCandidatesTabsEnum;
	setBulkSelectedCandidates: React.Dispatch<React.SetStateAction<string[]>>;
	tableColumns: candidateTableColumnHeaderType[];
}) => {
	const { id } = useParams();
	const queryClient = useQueryClient();

	const allHiringAssessments = queryClient?.getQueryState(['getAllHiringAssessments']) || { data: [] };

	const hiringAssessmentItem = (allHiringAssessments?.data as GetHiringAssessmentListResponse)?.hiringAssessmentList;

	const filteredAssessment = hiringAssessmentItem?.find(assessment => {
		return assessment?._id === id;
	});
	const qualificationCriteriaBySalary = filteredAssessment?.salaryRange?.min || 0;

	const maxLength = 15;
	const [searchParams] = useSearchParams();
	const currYear = new Date().getFullYear();
	const { themeMode } = useThemeContext();
	const isSelected = bulkSelectedCandidates.find(
		bulkCandidate => bulkCandidate?.toString() === studentData?._id?.toString(),
	)
		? true
		: false;
	const name = studentData?.name;
	const email = studentData?.email;
	const avatar = studentData.profile.proctoringImageUrl || '';
	const specialization =
		studentData?.pifMeta?.underGraduate?.specialisation || studentData?.pifMeta?.postGraduate?.specialisation;

	const collegeName =
		studentData?.pifMeta?.underGraduate?.college?.name || studentData?.pifMeta?.postGraduate?.college?.name;

	const assessmentType = studentData?.userPreference?.careerPath || 'NA';
	const isTrusted = studentData?.flags?.isTrusted;

	const percentile = studentData?.userPercentileBucket || 'NA';

	const matchedPercentaileLevel = studentData?.evaluation?.level || 0;

	const minPredictedSalary = studentData?.evaluation?.predictedSalary?.min;
	const maxPredictedSalary = studentData?.evaluation?.predictedSalary?.max;
	const salaryRangeToShow =
		typeof minPredictedSalary === 'number' && typeof maxPredictedSalary === 'number'
			? `₹${minPredictedSalary} - ${maxPredictedSalary}LPA`
			: 'NA';

	const qualifiedCandidatesAsPerSalaryRange = JSON.parse(searchParams.get('salary') || '[]');

	const isQualified = filteredAssessment ? maxPredictedSalary >= qualificationCriteriaBySalary : false;

	const isQualifiedForCampusCandidate =
		typeOfUsers !== TypeOfUsers.ASSESSMENT
			? qualifiedCandidatesAsPerSalaryRange?.length
				? parseInt(qualifiedCandidatesAsPerSalaryRange?.[0]?.split('_')[0]) <= maxPredictedSalary &&
				  parseInt(qualifiedCandidatesAsPerSalaryRange?.[0]?.split('_')[1]) >= minPredictedSalary
				: false
			: false;

	const getBadgesWithSkills = () => {
		const badgesWithSkills = badgesList.map(badge => {
			const filteredSkills = (
				studentData?.evaluation?.skillRatings?.length ? studentData?.evaluation?.skillRatings || [] : []
			).filter(item => {
				const score = isScoreRangeValid(Math.round(item.rating));
				return score >= badge.minScore && score < badge.maxScore;
			});
			return { ...badge, skills: filteredSkills };
		});

		// Sort badges by the number of skills, descending
		return badgesWithSkills
			.sort((a, b) => b.minScore - a.minScore)
			?.filter(el => el.skills.length)
			.slice(0, 2);
	};

	const badeges = getBadgesWithSkills();

	return (
		<div
			onClick={() => {
				if (bulkSelect) {
					if (isSelected) {
						setBulkSelectedCandidates(prev =>
							prev.filter(bulkCandidate => bulkCandidate?.toString() !== studentData?._id?.toString()),
						);
						return;
					}
					setBulkSelectedCandidates(prev => [...prev, studentData?._id?.toString()]);
				}
			}}
			className={classNames(
				'relative rounded-[12px] border border-[#EBEBF0] bg-white shadow-[0px_1px_2px_0px_rgba(226,_226,_226,_0.00),_0px_1px_3px_0px_rgba(167,_167,_167,_0.09)]',
				bulkSelect ? 'cursor-pointer' : 'cursor-auto',
			)}
		>
			{bulkSelect ? (
				<FaRegCircleCheck
					className={`w-[24px] h-[24px] absolute right-[-7px] pointer-events-none ${
						isSelected ? 'bg-[#7777E7] text-[#CCDDFF]' : 'bg-white text-[#555770]'
					} rounded-full top-[-7px] `}
				/>
			) : null}

			{/* {percentile ? (
				<div className="absolute top-4 right-4">
					<div className="text-xs w-fit py-[2px] px-2 mx-auto text-[#00B7C4] rounded-full border border-[#00B7C4] bg-[#E5FFFF] flex text-center justify-center items-center">
						{percentile === UserPercentileBucket.TOP_ONE
							? 'Top 1%'
							: percentile === UserPercentileBucket.TOP_TEN
							  ? 'Top 10%'
							  : percentile === UserPercentileBucket.TOP_TWENTY_FIVE
							    ? 'Top 25%'
							    : percentile === UserPercentileBucket.TOP_FIFTY
							      ? 'Top 50%'
							      : 'NA'}
					</div>
				</div>
			) : null} */}

			{matchedPercentaileLevel ? (
				<div className="absolute top-4 right-0 ml-2">
					<div className="w-[150px] m-auto">
						<MatchPercentileCircularBar value={matchedPercentaileLevel} />
					</div>
				</div>
			) : null}

			<div className="flex items-center gap-2 p-4 pb-1">
				<div className="min-w-[48px] max-w-[48px] min-h-[48px] max-h-[48px] relative">
					<Avatar
						rounded
						theme={{
							root: {
								size: {
									md: 'h-12 w-12',
								},
							},
						}}
						className="text-xl flex max-w-[48px] max-h-[48px] min-w-[48px] min-h-[48px] h-full w-full"
						img={avatar || ''}
						placeholderInitials={stringAvatar(
							showCandidateName
								? studentData?.name?.trim()?.toUpperCase() || ''
								: studentData?.email?.trim()?.toUpperCase(),
						)}
					/>
					{(isQualified || isQualifiedForCampusCandidate) && (
						<div className="absolute bottom-0 left-[50%] translate-x-[-50%] h-2">
							<img src="/assets/reco.svg" alt="verified-tag" />
						</div>
					)}
				</div>

				<div>
					<div className="dark:text-white">
						<div className="flex gap-1">
							{name.length > maxLength ? (
								<div>
									{showCandidateName ? (
										<Tooltip title={name} placement="top">
											<a
												target="_blank"
												href={`${process.env.REACT_APP_APP_ENDPOINT}/profile/${studentData.handler}?company=true`}
												className="cursor-pointer underline text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
											>
												<span>{name?.toLowerCase()?.substring(0, maxLength)}...</span>
												{isTrusted ? (
													<span>
														<div className="flex flex-col justify-center">
															<LightTooltip
																light={!(themeMode === 'dark')}
																title={<VerifiedTooltip />}
																placement="bottom"
																arrow={true}
																componentsProps={{
																	tooltip: {
																		sx: {
																			border: '0px !important',
																			borderRadius: '4px !important',
																			boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																		},
																	},
																}}
															>
																<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
															</LightTooltip>
														</div>
													</span>
												) : null}
											</a>
										</Tooltip>
									) : null}
									{studentData?.flags?.isGuestUser ? (
										<span className="my-0.5 inline-flex w-fit items-center rounded-full bg-[#FFF4E0] px-2 py-0.5 text-[11px] font-[600] text-[#B54708]">
											Guest user
										</span>
									) : null}
									{email ? <p className="text-[#555770] text-xs">{email}</p> : null}
									{specialization || studentData?.profile?.gradYear?.toString().length > 0 ? (
										<p className="text-[#555770] text-xs">
											<div className="flex gap-1 items-center">
												<div>
													<HiAcademicCap className="text-[#00B7C4] dark:text-[#A9EFF2] w-[12px] md:w-[20px] h-[12px] md:h-[20px]" />
												</div>

												<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px] max-w-[20ch] truncate">
													{specialization ? (
														<>
															<LightTooltip
																light={!(themeMode === 'dark')}
																title={
																	<div className="p-2 text-sm text-center text-[#555770] font-[500]">
																		{specialization}
																	</div>
																}
																placement="bottom"
																arrow={true}
																componentsProps={{
																	tooltip: {
																		sx: {
																			border: '0px !important',
																			borderRadius: '4px !important',
																			boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																		},
																	},
																}}
															>
																<span className="max-w-[20ch] truncate">{specialization} </span>
															</LightTooltip>
															(
															{studentData?.profile?.gradYear === -1
																? '2015'
																: studentData?.profile?.gradYear === 1
																  ? '2027'
																  : studentData?.profile?.gradYear <= currYear
																    ? studentData?.profile?.gradYear
																    : studentData?.profile?.gradYear || 'future'}
															)
														</>
													) : studentData?.profile?.gradYear === -1 ? (
														'Graduated before 2015'
													) : studentData?.profile?.gradYear === 1 ? (
														'Graduating after 2027'
													) : studentData?.profile?.gradYear <= currYear ? (
														`Graduated in ${studentData?.profile?.gradYear}`
													) : (
														`Graduating in ${studentData?.profile?.gradYear || 'future'}`
													)}
												</div>
											</div>
										</p>
									) : null}
									{collegeName ? (
										<LightTooltip
											light={!(themeMode === 'dark')}
											title={<div className="p-2 text-sm text-center text-[#555770] font-[500]">{collegeName}</div>}
											placement="bottom"
											arrow={true}
											componentsProps={{
												tooltip: {
													sx: {
														border: '0px !important',
														borderRadius: '4px !important',
														boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
													},
												},
											}}
										>
											<p className="text-[#555770] text-xs ml-[10%] truncate max-w-[20ch]">{collegeName}</p>
										</LightTooltip>
									) : null}
								</div>
							) : (
								<div>
									{showCandidateName ? (
										<a
											target="_blank"
											href={`${process.env.REACT_APP_APP_ENDPOINT}/profile/${studentData.handler}?company=true`}
											className="underline cursor-pointer text-[18px] font-[600] text-[#1C1C28] flex items-center gap-1 capitalize"
										>
											<span>{name?.toLowerCase()}</span>
											{isTrusted ? (
												<span>
													<div className="flex flex-col justify-center">
														<LightTooltip
															light={!(themeMode === 'dark')}
															title={<VerifiedTooltip />}
															placement="bottom"
															arrow={true}
															componentsProps={{
																tooltip: {
																	sx: {
																		border: '0px !important',
																		borderRadius: '4px !important',
																		boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																	},
																},
															}}
														>
															<img src="/assets/verified-tag.svg" alt={'verified-tag'} width={14} height={14} />
														</LightTooltip>
													</div>
												</span>
											) : null}
										</a>
									) : null}
									{studentData?.flags?.isGuestUser ? (
										<span className="my-0.5 inline-flex w-fit items-center rounded-full bg-[#FFF4E0] px-2 py-0.5 text-[11px] font-[600] text-[#B54708]">
											Guest user
										</span>
									) : null}
									{email ? <p className="text-[#555770] text-xs">{email}</p> : null}
									{specialization || studentData?.profile?.gradYear?.toString().length > 0 ? (
										<p className="text-[#555770] text-xs">
											<div className="flex gap-1">
												<div>
													<HiAcademicCap className="text-[#00B7C4] dark:text-[#A9EFF2] w-[16px] md:w-[20px] h-[16px] md:h-[20px]" />
												</div>

												<div className="text-[#28293D] dark:text-[#E4E4EB] text-xs md:text-[14px] max-w-[20ch] truncate">
													{specialization ? (
														<>
															<LightTooltip
																light={!(themeMode === 'dark')}
																title={
																	<div className="p-2 text-sm text-center text-[#555770] font-[500]">
																		{specialization}
																	</div>
																}
																placement="bottom"
																arrow={true}
																componentsProps={{
																	tooltip: {
																		sx: {
																			border: '0px !important',
																			borderRadius: '4px !important',
																			boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
																		},
																	},
																}}
															>
																<span className="max-w-[20ch] truncate">{specialization} </span>
															</LightTooltip>
															(
															{studentData?.profile?.gradYear === -1
																? '2015'
																: studentData?.profile?.gradYear === 1
																  ? '2027'
																  : studentData?.profile?.gradYear <= currYear
																    ? studentData?.profile?.gradYear
																    : studentData?.profile?.gradYear || 'future'}
															)
														</>
													) : studentData?.profile?.gradYear === -1 ? (
														'Graduated before 2015'
													) : studentData?.profile?.gradYear === 1 ? (
														'Graduating after 2027'
													) : studentData?.profile?.gradYear <= currYear ? (
														`Graduated in ${studentData?.profile?.gradYear}`
													) : (
														`Graduating in ${studentData?.profile?.gradYear || 'future'}`
													)}
												</div>
											</div>
										</p>
									) : null}
									{collegeName ? (
										<LightTooltip
											light={!(themeMode === 'dark')}
											title={<div className="p-2 text-sm text-center text-[#555770] font-[500]">{collegeName}</div>}
											placement="bottom"
											arrow={true}
											componentsProps={{
												tooltip: {
													sx: {
														border: '0px !important',
														borderRadius: '4px !important',
														boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
													},
												},
											}}
										>
											<p className="text-[#555770] text-xs ml-[10%] truncate max-w-[20ch]">{collegeName}</p>
										</LightTooltip>
									) : null}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="px-4 py-[10px] grid grid-cols-2 gap-x-[6px] gap-y-[10px]">
				<div className="flex-1">
					<Label className="flex text-[10px] text-[#555770]">Role</Label>
					<div className="mt-[2px] flex items-start leading-5 text-center">
						{assessmentType === 'NA' ? (
							<div className="text-sm">NA</div>
						) : (
							<div
								className={classNames(
									'self-stretch rounded-[4.52px] py-[1px] px-[9px] text-[8.788px] font-medium break-words',
									getBgColor(assessmentType),
								)}
							>
								{formatCareerPathType(assessmentType as UserCareerPathType)}
							</div>
						)}
					</div>
				</div>
				{/* <div className="flex-1">
					<Label className="text-[10px] text-[#555770] flex gap-1 items-center">
						Predicted salary{' '}
						<span className="text-[12px]">
							<LightTooltip
								light={!(themeMode === 'dark')}
								title={
									<div className="p-2 text-sm text-center text-[#555770] font-[500]">
										{tableColumns?.find(column => column.id === 'predicted_salary')?.info || ''}
									</div>
								}
								placement="bottom"
								arrow={true}
								componentsProps={{
									tooltip: {
										sx: {
											border: '0px !important',
											borderRadius: '4px !important',
											boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
										},
									},
								}}
							>
								<span>
									<PiInfoFill />
								</span>
							</LightTooltip>
						</span>
					</Label>
					<div className="text-[14px] font-[500] text-[#1F3343]">
						{salaryRangeToShow === 'NA' ? (
							'NA'
						) : (
							<div className="flex flex-col">
								<p>{salaryRangeToShow}</p>
							</div>
						)}
					</div>
				</div> */}

				<div className="flex-1 w-full col-span-2">
					<Label className="text-[10px] text-[#555770] flex gap-1 items-center mb-1">
						Skills{' '}
						<span className="text-[12px]">
							<LightTooltip
								light={!(themeMode === 'dark')}
								title={
									<div className="p-2 text-sm text-center text-[#555770] font-[500]">
										{tableColumns?.find(column => column.id === 'skill_level')?.info || ''}
									</div>
								}
								placement="bottom"
								arrow={true}
								componentsProps={{
									tooltip: {
										sx: {
											border: '0px !important',
											borderRadius: '4px !important',
											boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
										},
									},
								}}
							>
								<span>
									<PiInfoFill />
								</span>
							</LightTooltip>
						</span>
					</Label>
					{!badeges?.length ? (
						<div className="text-sm">NA</div>
					) : (
						<div className="flex gap-[18px] text-center mx-auto">
							{badeges?.map((badege, index) => (
								<div key={index} className="flex gap-[3px]">
									<div
										className="flex items-center gap-1 text-sm rounded-[6px] px-2 py-[1px]"
										style={{ background: badege?.bg, color: badege?.text }}
									>
										<img src={badege?.icon} alt="" className="max-w-[15px] max-h-[15px]" />
										{badege?.title}
									</div>
									{badege?.skills?.length ? (
										<Tooltip
											title={
												<div className="text-center">
													{badege?.skills?.map(skill => skill?.skill?.name)?.join(', ')}
												</div>
											}
											arrow
											placement="bottom"
										>
											<div className="flex justify-center border-b border-[#555770] mx-auto border-dashed h-fit w-fit text-center text-[11.2px] font-[500] text-[#555770]">
												{badege?.skills?.length ? `${badege?.skills?.length} skills` : ''}
											</div>
										</Tooltip>
									) : null}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{typeOfUsers === TypeOfUsers.DRIVE ? (
				<div className="bg-[#FAFAFC] border-t border-[#EBEBF0] rounded-b-[12px] px-[12px] py-[10px]">
					<DriveCandidatesActionButton selectedTab={selectedTab as CandidatesTabsEnum} studentData={studentData} />
				</div>
			) : (
				<div className="bg-[#FAFAFC] border-t border-[#EBEBF0] rounded-b-[12px] px-[12px] py-[10px]">
					{typeOfUsers === TypeOfUsers.FRESHER && selectedTab === PositionCandidatesTabsEnum.CONNECTED ? (
						<div>
							<FresherActionButton
								showActionButton={true}
								showRoundProgress={true}
								selectedTab={selectedTab}
								studentData={studentData}
								isPositionCreated={isPositionCreated}
							/>
						</div>
					) : null}

					<div>
						{typeOfUsers === TypeOfUsers.FRESHER && selectedTab !== PositionCandidatesTabsEnum.CONNECTED ? (
							<FresherActionButton
								selectedTab={selectedTab}
								studentData={studentData}
								isPositionCreated={isPositionCreated}
							/>
						) : typeOfUsers !== TypeOfUsers.FRESHER ? (
							<div className="flex flex-wrap items-center justify-between gap-4">
								<CampusCandidateActionButton
									disable={
										bulkSelect ||
										(typeOfUsers === TypeOfUsers.ASSESSMENT && studentData?.assessmentStatus === MeetingStatus.PENDING)
									}
									studentData={studentData}
									showCandidateName={showCandidateName}
									totalShortlistedCandidates={totalShortlistedCandidates}
									showHriingAssessmentUsersOnly={typeOfUsers === TypeOfUsers.ASSESSMENT}
									showShortlistingButton={selectedTab === CandidatesTabsEnum.RECOMMENDATIONS}
									ctaText={typeOfUsers === TypeOfUsers.ASSESSMENT ? 'Schedule Interview' : 'Invite for Drive'}
								/>
							</div>
						) : null}
					</div>
				</div>
			)}
		</div>
	);
};

export default CandidateCardMobile;
